<script>
  import { format } from "date-fns";
  import { filtersConfigStore } from "@stores/damFiltersConfigStores.js";
  import { isValidRegex } from "../../../../../../lib/util/regex";
  import {
    DatePicker,
    DatePickerInput,
    TimePicker,
    Select,
    SelectItem,
    MultiSelect,
    NumberInput,
    Tooltip as CarbonTooltip,
  } from "carbon-components-svelte";
  import { Tooltip, TextInput, Button } from "paxray-components-svelte";
  import { filter } from "d3";

  export let titleTab = "";
  export let filterCreate;
  export let isLoadingFilter = false;
  export let dateTimeIntervalStructure = {
    startDate: format(new Date(), "dd/MM/yyyy"),
    endDate: format(new Date(), "dd/MM/yyyy"),
    startDataTime: "00:00",
    endDataTime: "00:00",
    startTime: "00:00",
    endTime: "00:00",
  };

  export let dateTimeStructure = {
    date: format(new Date(), "dd/MM/yyyy"),
    time: "00:00",
  };
  export let timeStructure = {
    hours: NaN,
    mins: NaN,
    secs: NaN,
  };

  let { startDate, endDate, startDataTime, endDataTime, startTime, endTime } =
    dateTimeIntervalStructure;
  let { date, time } = dateTimeStructure;
  let { hours, mins, secs } = timeStructure;
  let addTimePicker = endDataTime !== "00:00" || startDataTime != "00:00";
  let validEndDate = false;
  let validStartTime = true;
  let validEndTime = true;
  let openHint = false;

  const { mergeColumnDefinitions, getFilterByCurrentView } = filtersConfigStore;

  let selectedIds = [];
  let logColumns = [];
  let datatype = "";
  let columnDetails = [];
  let operatorList;
  let selectValues;
  let listOfValues = [];
  let columnLabel = "Column";

  $: ({ storeLoaded } = $filtersConfigStore);

  $: validEndDate = isInvalidDate(startDate, endDate);
  $: validStartTime = isInvalidTime(startTime);
  $: validEndTime = isInvalidTime(endTime);
  $: validTime = isInvalidTime(time);
  $: validDate = isInvalidStartDate(date);

  $: resetFilterCreate(titleTab);
  $: if (storeLoaded && titleTab) {
    columnDetails = getColumnDetails(titleTab);
    logColumns = Object.entries(columnDetails)
      // Clear Name is replaced in the UI by the technical application name.
      .filter(([key]) => key !== "clearname")
      .map(([key, value]) => ({
          key,
          title: key === "applicationname"
            ? "App/Website"
            : key === "windowtitle" || value.title === "Window Title"
              ? "Mask"
            : value.title,
        }));
  }

  $: columnLabel = titleTab === "KPI Filter"
    ? "Metric"
    : filterCreate.col === "applicationname"
      ? "App/Website"
      : "Value";

  $: modCol = filterCreate.col;

  $: setOperatorList(modCol);
  $: setTimeValue(startTime, endTime);
  $: setDateTimeIntervalValue(startDate, endDate, startDataTime, endDataTime);
  $: setDateTimeValue(date, time);
  $: setSecondsValue(hours, mins, secs);
  $: setSelectValues(modCol);

  $: comparison = filterCreate.comparison;

  $: resetValue(comparison);

  $: setDataTypeinFilterCreate(filterCreate.comparison);

  $: isInvalidRegex =
    filterCreate.comparison === "regex"
      ? !isValidRegex(filterCreate.value)
      : false;

  $: setValuesFromMultiSelect(selectedIds);

  $: if ($isLoadingFilter) {
    setSelectedIdsFromValues(filterCreate.value);
  }

  function resetValue(comparison) {
    const isMultiselect = comparison === "in" || comparison === "notin";
    if (isMultiselect && !Array.isArray(filterCreate.value))
      filterCreate.value = "";
    if (!isMultiselect && Array.isArray(filterCreate.value)) selectedIds = [];
  }

  function setSecondsValue(hours, mins, secs) {
    if ($isLoadingFilter) return;
    if (filterCreate.datatype !== "duration") return;
    filterCreate.value = "";
    if (hours === null) hours = NaN;
    if (mins === null) mins = NaN;
    if (secs === null) secs = NaN;
    if (isNaN(hours) && isNaN(mins) && isNaN(secs)) {
      filterCreate.value = "";
      return;
    }
    const validHours = isNaN(hours) ? 0 : parseInt(hours);
    const validMins = isNaN(mins) ? 0 : parseInt(mins);
    const validSecs = isNaN(secs) ? 0 : parseInt(secs);
    const totalSeconds = validHours * 3600 + validMins * 60 + validSecs;
    filterCreate.value = totalSeconds;
  }

  function setTimeValue(startTime, endTime) {
    if ($isLoadingFilter) return;
    if (filterCreate.datatype !== "timeInterval") return;
    filterCreate.value = "";
    if (!isInvalidTime(startTime) && !isInvalidTime(endTime)) {
      filterCreate.value = [startTime, endTime];
    }
  }

  function setDateTimeValue(date, time) {
    if ($isLoadingFilter) return;
    if (
      filterCreate.datatype !== "datetime" &&
      filterCreate.datatype !== "date" &&
      filterCreate.datatype !== "time"
    )
      return;
    filterCreate.value = "";
    if (filterCreate.datatype === "date" && !isInvalidStartDate(date)) {
      filterCreate.value = date.split("/").reverse().join("-");
      return;
    }
    if (filterCreate.datatype === "time" && !isInvalidTime(time)) {
      filterCreate.value = time;
      return;
    }
    if (!isInvalidStartDate(date) && !isInvalidTime(time)) {
      filterCreate.value = `${date.split("/").reverse().join("-")}T${time}`;
    }
  }

  function setDateTimeIntervalValue(
    startDate,
    endDate,
    startDataTime,
    endDataTime
  ) {
    if ($isLoadingFilter) return;
    if (
      filterCreate.datatype !== "datetimeInterval" &&
      filterCreate.datatype !== "datetime" &&
      filterCreate.datatype !== "date" &&
      filterCreate.datatype !== "time" &&
      filterCreate.datatype !== "dateInterval"
    )
      return;
    filterCreate.value = "";
    if (
      filterCreate.datatype === "datetime" &&
      !isInvalidStartDate(startDate) &&
      !isInvalidTime(startDataTime)
    ) {
      filterCreate.value =
        startDate.split("/").reverse().join("-") + "T" + startDataTime;
      return;
    }
    if (filterCreate.datatype === "date" && !isInvalidStartDate(startDate)) {
      filterCreate.value = startDate.split("/").reverse().join("-");
      return;
    }
    if (filterCreate.datatype === "time" && !isInvalidTime(startDataTime)) {
      filterCreate.value = startDataTime;
      return;
    }
    if (
      filterCreate.datatype === "dateInterval" &&
      !isInvalidDate(startDate, endDate)
    ) {
      filterCreate.value = [
        startDate.split("/").reverse().join("-"),
        endDate.split("/").reverse().join("-"),
      ];
      return;
    }
    if (
      !isInvalidDate(startDate, endDate) &&
      !isInvalidTime(startDataTime) &&
      !isInvalidTime(endDataTime)
    ) {
      const startFinalDateTime = `${startDate.split("/").reverse().join("-")}T${startDataTime}`;
      const endFinalDateTime = `${endDate.split("/").reverse().join("-")}T${endDataTime}`;
      filterCreate.value = [startFinalDateTime, endFinalDateTime];
    }
  }

  function resetFields() {
    if ($isLoadingFilter) return;
    operatorList = [];
    selectValues = [];
    filterCreate.datatype = "";
    filterCreate.value = "";
    filterCreate.comparison = "";
    endTime = "00:00";
    startTime = "00:00";
    selectedIds = [];
    date = format(new Date(), "dd/MM/yyyy");
    time = "00:00";
  }

  function setOperatorList(col) {
    if (!$isLoadingFilter) resetFields();
    if (col === "") {
      filterCreate.comparison = "";
      return;
    }
    const { dataTypesWithTitles, filterColumnDefinitions } =
      $filtersConfigStore;
    const originalDatatype = filterColumnDefinitions[col]?.datatype;
    datatype =
      filterCreate.datatype === originalDatatype
        ? filterCreate.datatype
        : columnDetails[col]?.datatype ||
          Object.values(columnDetails).find((colDet) => {
            return colDet.columns && colDet.columns === col;
          }).datatype;
    if (Array.isArray(datatype)) {
      operatorList = dataTypesWithTitles[datatype[0]];
      operatorList = [...operatorList, ...dataTypesWithTitles[datatype[1]]];
    } else {
      operatorList = dataTypesWithTitles[datatype];
      filterCreate.datatype = datatype;
    }
    if (!operatorList || operatorList.length === 0) {
      filterCreate.comparison = "";
      return;
    }
    if (["applicationname", "windowtitle"].includes(col)) {
      operatorList = operatorList.filter(({ operator }) =>
        ["in", "notin"].includes(operator)
      );
    }
    if (col === "activity") {
      operatorList = operatorList.filter(({ operator }) =>
        ["in", "notin", "eq", "ne"].includes(operator)
      );
    }
    if (operatorList.length === 0) {
      filterCreate.comparison = "";
      return;
    }
    if (
      !operatorList.some(
        ({ operator }) => operator === filterCreate.comparison
      )
    ) {
      filterCreate.comparison = operatorList[0].operator;
    }
  }

  function setSelectedIdsFromValues(values) {
    const valuesWithID = columnDetails[filterCreate.col]?.valuesWithID || false;
    if (filterCreate.col === "applicationname") {
      selectedIds = Array.isArray(values) ? values : [];
      return;
    }
    if (valuesWithID) {
      selectedIds = values;
    } else {
      const multiSelectValues = getMultiSelectValues(filterCreate.col);
      multiSelectValues.forEach((value) => {
        if (values.includes(value.text)) {
          selectedIds.push(value.id);
        }
      });
    }
  }

  function setValuesFromMultiSelect(selectedIds) {
    if ($isLoadingFilter) return;
    const valuesWithID = columnDetails[filterCreate.col]?.valuesWithID || false;

    if (filterCreate.col === "applicationname") {
      filterCreate.value = selectedIds;
      return;
    }

    if (valuesWithID) {
      filterCreate.value = selectedIds;
      return;
    }
    const listOfValues = getMultiSelectValues(filterCreate.col);
    if (filterCreate.datatype !== "multiSelect") return;
    filterCreate.value = listOfValues
      .filter((value) => selectedIds.includes(value.id))
      .map((value) => value.text);
  }

  function setDataTypeinFilterCreate(comparison) {
    if ($isLoadingFilter) return;
    if (!Array.isArray(datatype) || !comparison) return;
    const { dataTypesWithTitles } = $filtersConfigStore;
    const selectedOperator = comparison;

    const correspondDataType = Object.entries(dataTypesWithTitles).find(
      ([key, value]) =>
        value.find(
          (op) => op.operator === selectedOperator && datatype.includes(key)
        )
    )[0];
    filterCreate.datatype = correspondDataType;
  }

  function setSelectValues(col) {
    if (filterCreate.datatype !== "select") return;
    const { filterColumnDefinitions } = $filtersConfigStore;
    const values = filterColumnDefinitions[col]?.values || [];
    if (values.length > 0) {
      selectValues = values.map((value, index) => {
        return { id: index, text: value };
      });
    } else {
      selectValues = [];
    }
  }

  function getOperatorTitle(operator, title) {
    if (!["team", "applicationname", "windowtitle"].includes(filterCreate.col))
      return title;
    if (operator === "in") return "=";
    if (operator === "notin") return "≠";
    return title;
  }

  function getColumnTitle(col) {
    const title = columnDetails[col]?.title || "";
    if (col === "applicationname" || title === "Application Name") return "App/Website";
    if (col === "windowtitle" || title === "Window Title") return "Mask";
    return title;
  }

  function getMetricTooltip(metric) {
    const descriptions = {
      nactivities: "Number of activities in the case.",
      numberOfUniqueTeams: "Number of different teams involved in the case.",
      numberOfHandovers: "Number of handovers between teams in the case.",
      lastactivity: "Timestamp of the last activity in the case.",
      duration: "Total duration of the case from its start to its last activity.",
    };

    return descriptions[metric] || "Select a metric, then define its comparison and value.";
  }

  function resetFilterCreate(titleTab) {
    if ($isLoadingFilter) return;
    filterCreate.col =
      titleTab === "Value Filter"
        ? "team"
        : titleTab === "KPI Filter"
          ? "duration"
          : "";
    operatorList = [];
    filterCreate.comparison = "";
    filterCreate.datatype = "";
    filterCreate.value = "";
  }

  function getColumnDetails(titleTab) {
    const {
      filterDefinitions,
      filterColumnDefinitions,
      currentView,
      filterAllowedOperators,
      filterOperators,
      tab,
      views,
    } = $filtersConfigStore;
    const filtersByView = getFilterByCurrentView(
      views,
      filterDefinitions,
      filterColumnDefinitions,
      filterAllowedOperators,
      filterOperators,
      currentView,
      tab
    );

    let filterKey =
      filtersByView?.find((filter) => filter.title === titleTab)?.filterKey ||
      "";

    const details = mergeColumnDefinitions(
      filterDefinitions?.[filterKey] || [],
      filterColumnDefinitions || []
    );

    if (titleTab === "KPI Filter") {
      const legacyKpiDefinitions = {
        lastcw: { title: "Last CW", datatype: "number" },
        nactivities: { title: "Activities", datatype: "number" },
        numberOfUniqueTeams: { title: "Team(s)", datatype: "number" },
        numberOfHandovers: { title: "Handovers", datatype: "number" },
        lastactivity: { title: "Last Activity", datatype: "date" },
        duration: { title: "Duration", datatype: "duration" },
      };

      Object.entries(legacyKpiDefinitions).forEach(([column, fallback]) => {
        details[column] = details[column] || filterColumnDefinitions?.[column] || fallback;
      });
    }

    return details;
  }

  function hasValues(col) {
    const values = columnDetails[col]?.values || [];
    if (Object.keys(values).length > 0) {
      listOfValues = Object.values(values);
    } else {
      listOfValues = [];
    }
    return Object.keys(values).length > 0;
  }

  function getMultiSelectValues(col) {
    const values = columnDetails[col]?.values || [];
    if (!values) return [];
    const valuesWithID = columnDetails[col]?.valuesWithID || false;
    if (valuesWithID) {
      return values.map((value) => {
        return { id: value.id, text: value.title };
      });
    }
    if (col === "applicationname") {
      const mappings = $filtersConfigStore.appnameMappings || [];
      const clearNamesByApplication = new Map(
        mappings.map((mapping) => [
          String(mapping.applicationname),
          mapping.clearname || mapping.applicationname,
        ]),
      );
      return values.map((value) => ({
        id: value,
        text: clearNamesByApplication.get(String(value)) || value,
      }));
    }
    return values.map((value, index) => {
      return { id: index, text: value };
    });
  }

  function handleTimeFilter() {
    addTimePicker = !addTimePicker;
    if (!addTimePicker) {
      startDataTime = "00:00";
      endDataTime = "00:00";
    }
  }

  function isInvalidDate(startDate, endDate) {
    const start = new Date(startDate.split("/").reverse().join("-"));
    const end = new Date(endDate.split("/").reverse().join("-"));

    const startYear = startDate.split("/").map(Number)[2];
    const endYear = startDate.split("/").map(Number)[2];

    return (
      start > end ||
      startYear < 1680 ||
      endYear < 1680 ||
      startYear > 2260 ||
      endYear > 2260
    );
  }

  function isInvalidStartDate(startDate) {
    const start = new Date(startDate.split("/").reverse().join("-"));
    const startYear = startDate.split("/").map(Number)[2];
    return startYear < 1680 || startYear > 2260;
  }

  function isInvalidTime(time) {
    const pattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
    return !pattern.test(time) && time !== "";
  }

  function setSelectedValues() {
    const values = Object.entries(getMultiSelectValues(filterCreate.col))
      .filter(([key, value]) => selectedIds.includes(value.id))
      .map(([key, value]) => value.text);
    return '"' + values.join('" , "') + '"';
  }

  function handle7days() {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    startDate = format(sevenDaysAgo, "dd/MM/yyyy");
    endDate = format(today, "dd/MM/yyyy");
  }

  function handle30days() {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    startDate = format(thirtyDaysAgo, "dd/MM/yyyy");
    endDate = format(today, "dd/MM/yyyy");
  }
</script>

<div
  class={`filter_wrapper ${
    titleTab === "KPI Filter"
      ? "metric-filter-wrapper"
      : titleTab === "Value Filter"
        ? filterCreate.datatype === "dateInterval"
          ? "value-filter-wrapper date-interval-wrapper"
          : filterCreate.datatype === "datetimeInterval"
            ? "value-filter-wrapper datetime-interval-wrapper"
          : "value-filter-wrapper"
        : ""
  }`}
>
  <div class={titleTab === "KPI Filter" ? "metric-selector" : ""}>
    {#if titleTab === "KPI Filter"}
      <div class="metric-label">
        <span class="metric-label-text">{columnLabel}</span>
        <Tooltip
          text={getMetricTooltip(filterCreate.col)}
        />
      </div>
    {/if}
    <Select
      bind:selected={filterCreate.col}
      labelText={columnLabel}
      hideLabel={titleTab === "KPI Filter"}
    >
      {#each logColumns as { key, title }}
        {#if !(titleTab === "KPI Filter" && key === "lastcw")}
          <SelectItem value={key} text={title}></SelectItem>
        {/if}
      {/each}
    </Select>
    {#if titleTab !== "KPI Filter" && columnDetails[filterCreate.col]?.hint}
      <div class="hint_wrapper">
        <CarbonTooltip bind:openHint align="start" class="hint" direction="right">
          {columnDetails[filterCreate.col].hint}
        </CarbonTooltip>
      </div>
    {/if}
  </div>
  {#if operatorList && operatorList.length > 0}
    <Select bind:selected={filterCreate.comparison} labelText="Comparison">
      {#each operatorList as { operator, title }}
        <SelectItem
          value={operator}
          text={getOperatorTitle(operator, title)}
        ></SelectItem>
      {/each}
    </Select>
  {/if}
  {#key `${filterCreate.col}`}
    {#if filterCreate.datatype === "number"}
      <NumberInput
        bind:value={filterCreate.value}
        hideSteppers
        label="Number"
      />
    {:else if filterCreate.datatype === "string"}
      <TextInput
        bind:value={filterCreate.value}
        labelText={getColumnTitle(filterCreate.col)}
        invalid={isInvalidRegex}
        invalidText="Invalid Regex"
        list={filterCreate.col}
        placeholder={hasValues(filterCreate.col)
          ? `All ${columnDetails[filterCreate.col].title}s`
          : ""}
      />
      {#if hasValues(filterCreate.col)}
        <datalist id={filterCreate.col}>
          {#each listOfValues.sort() as value}
            <option {value} />
          {/each}
        </datalist>
      {/if}
    {:else if filterCreate.datatype === "multiSelect"}
      <div class="multiselect">
        <MultiSelect
          class="multiselect-values"
          items={getMultiSelectValues(filterCreate.col)}
          label={selectedIds.length > 0
            ? setSelectedValues()
            : filterCreate.col === "team"
              ? "Select team or teams..."
              : "Select " + getColumnTitle(filterCreate.col) + "..."}
          titleText={getColumnTitle(filterCreate.col)}
          bind:selectedIds
        ></MultiSelect>
      </div>
    {:else if filterCreate.datatype === "timeInterval"}
      <div class="time_interval">
        <TimePicker
          labelText="Start Time"
          placeholder="hh:mm"
          bind:value={startTime}
          invalid={validStartTime}
        />
        <TimePicker
          labelText="End Time"
          placeholder="hh:mm"
          bind:value={endTime}
          invalid={validEndTime}
          class="time_picker"
        />
      </div>
    {:else if filterCreate.datatype === "datetimeInterval"}
      <div class="datetime_interval">
        <div>
          <DatePicker
            datePickerType="single"
            bind:value={startDate}
            dateFormat="d/m/Y"
            short
          >
            <DatePickerInput
              labelText="Start Date"
              placeholder="dd/mm/yyyy"
              invalidText="The end date cannot be before the start date."
              invalid={validEndDate}
            />
            {#if addTimePicker}
              <TimePicker
                labelText="Start Time"
                placeholder="hh:mm"
                bind:value={startDataTime}
                invalid={validStartTime}
              />
            {/if}
          </DatePicker>

          <DatePicker
            datePickerType="single"
            bind:value={endDate}
            dateFormat="d/m/Y"
            short
          >
            <DatePickerInput
              labelText="End Date"
              placeholder="dd/mm/yyyy"
              invalidText="Please select a valid time range. "
              invalid={validEndDate}
            />
            {#if addTimePicker}
              <TimePicker
                labelText="End Time"
                placeholder="hh:mm"
                bind:value={endDataTime}
                invalid={validEndTime}
              />
            {/if}
          </DatePicker>
        </div>
        <!-- <div class="button_time_picker">
          <Button
            text="{addTimePicker ? 'Remove' : 'Add'} Time Filter"
            kind="secondary"
            on:click={handleTimeFilter}

          />
        </div> -->
      </div>
      <!-- <div class="button_wrapper">
<Button text="Last 7 Days" kind="secondary" on:click={handle7days}
        />
<Button text="Last 30 Days" kind="secondary" on:click={handle30days}
        />
      </div> -->
    {:else if filterCreate.datatype === "dateInterval"}
      <div class="date_interval">
        <div class="date-field">
          <DatePicker
            datePickerType="single"
            bind:value={startDate}
            dateFormat="d/m/Y"
            short
          >
            <DatePickerInput
              labelText="Start Date"
              placeholder="dd/mm/yyyy"
              invalidText="The end date cannot be before the start date."
              invalid={validEndDate}
            />
          </DatePicker>
        </div>

        <div class="date-field">
          <DatePicker
            datePickerType="single"
            bind:value={endDate}
            dateFormat="d/m/Y"
            short
          >
            <DatePickerInput
              labelText="End Date"
              placeholder="dd/mm/yyyy"
              invalidText="Please select a valid time range. "
              invalid={validEndDate}
            />
          </DatePicker>
        </div>
      </div>
      <!-- <div class="button_wrapper">
<Button text="Last 7 Days" kind="secondary" on:click={handle7days}
        />
<Button text="Last 30 Days" kind="secondary" on:click={handle30days}
        />
      </div> -->
    {:else if filterCreate.datatype === "datetime"}
      <div class="datetime_interval">
        <DatePicker
          datePickerType="single"
          bind:value={date}
          dateFormat="d/m/Y"
          short
        >
          <DatePickerInput
            labelText="Date"
            placeholder="dd/mm/yyyy"
            invalidText="The date should be between 1680 and 2260."
            invalid={validDate}
          />
          {#if addTimePicker}
            <TimePicker
              labelText="Time"
              placeholder="hh:mm"
              bind:value={time}
              invalid={validTime}
            />
          {/if}
        </DatePicker>
        <!-- <div class="button_time_picker datetime">
          <Button
            text="{addTimePicker ? 'Remove' : 'Add'} Time Filter"
            kind="secondary"
            on:click={handleTimeFilter}

          />
        </div> -->
      </div>
    {:else if filterCreate.datatype === "date"}
      <div class="date">
        <DatePicker
          datePickerType="single"
          bind:value={date}
          dateFormat="d/m/Y"
          short
        >
          <DatePickerInput
            labelText="Date"
            placeholder="dd/mm/yyyy"
            invalidText="The date should be between 1680 and 2260."
            invalid={validDate}
          />
        </DatePicker>
      </div>
    {:else if filterCreate.datatype === "time"}
      <div class="time">
        <TimePicker
          labelText="Time"
          placeholder="hh:mm"
          bind:value={time}
          invalid={validTime}
        />
      </div>
    {:else if filterCreate.datatype === "duration"}
      <div class="time_wrapper">
        <NumberInput
          hideSteppers
          light
          label="Hours"
          bind:value={hours}
          allowEmpty
        />
        <NumberInput
          hideSteppers
          light
          label="Minutes"
          bind:value={mins}
          allowEmpty
        />
        <NumberInput
          hideSteppers
          light
          label="Seconds"
          bind:value={secs}
          allowEmpty
        />
      </div>
    {:else if filterCreate.datatype === "select"}
      <Select
        bind:selected={filterCreate.value}
        labelText="Select {getColumnTitle(filterCreate.col)}"
      >
        <SelectItem value="" text="" />
        {#if selectValues && selectValues.length > 0}
          {#each selectValues as { id, text }}
            <SelectItem value={text} {text}></SelectItem>
          {/each}
        {:else}
          <SelectItem value="" text="No values available" disabled></SelectItem>
        {/if}
      </Select>
    {/if}
  {/key}
</div>

<style>
  .filter_wrapper {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    width: 100%;
  }

  .metric-filter-wrapper,
  .value-filter-wrapper {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(14rem, 100%), 1fr));
    align-items: start;
    box-sizing: border-box;
    gap: 0.5rem;
    max-width: 100%;
    min-width: 0;
  }

  .value-filter-wrapper :global(.bx--form-item),
  .metric-filter-wrapper :global(.bx--form-item) {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .value-filter-wrapper :global(.bx--label),
  .metric-filter-wrapper :global(.bx--label) {
    margin-top: 0 !important;
    margin-bottom: 0.5rem !important;
    padding-top: 0 !important;
  }

  .value-filter-wrapper > div :global(.bx--select) {
    margin-top: -0.5rem !important;
  }

  .metric-filter-wrapper > .metric-selector :global(.bx--label),
  .metric-filter-wrapper > :global(.bx--form-item) :global(.bx--label) {
    margin-bottom: 0 !important;
  }

  .metric-filter-wrapper {
    overflow: hidden;
  }

  .date-interval-wrapper {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .date-interval-wrapper > .date_interval {
    display: contents !important;
    grid-template-columns: none;
  }

  .datetime-interval-wrapper {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .datetime-interval-wrapper > .datetime_interval,
  .datetime-interval-wrapper > .datetime_interval > div {
    display: contents !important;
    margin: 0;
  }

  .datetime-interval-wrapper :global(.bx--label) {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .datetime-interval-wrapper :global(.bx--date-picker),
  .datetime-interval-wrapper :global(.bx--date-picker__input),
  .datetime-interval-wrapper :global(.bx--time-picker),
  .datetime-interval-wrapper :global(.bx--time-picker__input),
  .datetime-interval-wrapper :global(.bx--form-item) {
    min-width: 0;
    max-width: 100%;
    width: 100% !important;
    box-sizing: border-box;
  }

  .metric-filter-wrapper > *,
  .value-filter-wrapper > * {
    min-width: 0;
    width: 100%;
  }

  .metric-label {
    display: flex;
    align-items: top;
    gap: var(--xs);
    margin-bottom: var(--xs);
  }

  .metric-label-text {
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    letter-spacing: 0.02em;
    text-align: left;
  }

  .metric-filter-wrapper :global(.bx--form-item),
  .metric-filter-wrapper :global(.bx--select),
  .metric-filter-wrapper :global(.bx--select-input__wrapper),
  .metric-filter-wrapper :global(.bx--label) {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .metric-filter-wrapper :global(.bx--select-input) {
    border-top: 0 !important;
  }

  .value-filter-wrapper :global(.bx--label) {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .filter_wrapper > * {
    min-width: 0;
  }

  .filter_wrapper > :first-child,
  .filter_wrapper > :last-child {
    flex: 3 1 0;
  }

  .filter_wrapper > :nth-child(2) {
    flex: 1 1 0;
  }

  :global(.multiselect .bx--list-box__label) {
    max-width: 28rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 3rem;
  }

  .button_time_picker {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .time_interval {
    display: flex;
    width: 100%;
    gap: 1rem;
  }

  :global(.time_interval .bx--form-item) {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    margin: 0;
  }

  :global(.time_interval .bx--time-picker) {
    width: 100%;
  }

  .datetime_interval {
    display: flex;
    gap: 1.5rem;
    justify-content: space-between;
    margin: 0 1.5rem;
  }

  .date_interval {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 1rem;
  }

  .date-field {
    min-width: 0;
    width: 100%;
    margin: 0;
  }

  :global(.date-field .bx--date-picker),
  :global(.date-field .bx--date-picker__input) {
    width: 100%;
  }

  .date,
  .time {
    margin-left: 1.5rem;
  }

  .time_wrapper {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 0.5rem !important;
    row-gap: 0.5rem !important;
    position: relative;
    min-width: 0;
    width: 100%;
  }

  .time_wrapper > * {
    margin: 0 !important;
    min-width: 0;
  }

  .time_wrapper :global(.bx--label) {
    margin: 0 0 0.5rem !important;
    padding: 0 !important;
  }

  .time_wrapper :global(.bx--form-item),
  .time_wrapper :global(.bx--number),
  .time_wrapper :global(.bx--number-input),
  .time_wrapper :global(.bx--number-input__field-wrapper),
  .time_wrapper :global(.bx--number-input__field),
  .time_wrapper :global(.bx--number__input) {
    min-width: 0;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
  }

  .button_wrapper {
    grid-column: 3;
    display: flex;
    gap: 1rem;
    justify-content: flex-start;
    margin: 1rem 0;
    margin-left: 1.5rem;
  }

  :global(
      .datetime_interval
        .bx--date-picker.bx--date-picker--single
        .bx--date-picker__input
    ) {
    width: 16rem;
  }

  .button_time_picker.datetime {
    align-items: end;
  }

  .hint_wrapper {
    margin-top: 0.5rem;
  }
</style>
